import type { ProductPromotion } from '#root/api/clients/product/data-contracts'
import type { ProductUpsell } from '#types/product'

/**
 * Fetches the applicable promotion plan for a product (GLOBAL15-131755) and extracts the `BUNDLE_PROMO`
 * entry, if any, mapped into `ProductUpsell`-shaped data for the Discounted Upsell Bundle PDP section.
 *
 * @param id - Product ID; reactively observed, refetches on change (e.g. PDP-to-PDP client navigation)
 * @returns Object containing:
 *   - `promotionPlan`: Raw API response (shipping/order/product promotion arrays)
 *   - `promotionPlanPending`: True while the request is pending
 *   - `promotionPlanError`: Error object if the fetch fails (non-fatal - bundle section just doesn't render)
 *   - `bundlePromo`: The first `BUNDLE_PROMO` entry in `promotionPlan.product`, if any
 *   - `bundleUpsells`: `bundlePromo`'s qualified products, mapped to `ProductUpsell[]` for rendering
 *
 * @remarks
 * - Client-only, lazy fetch (mirrors `useProduct`'s inventory call) - doesn't block PDP SSR render.
 * - `qualifiedProducts[].id` is a variant-specific SFCC id; `varitionGroupId` (sic) is the style-level
 *   product id and is what's used here as `ProductUpsell.id`/`sku` - it's what Quick Shop needs to fetch
 *   full product details, matching how `bundleSelection` upserts key by product id.
 * - The payload has no dedicated `sku`; the style id doubles for it. This is unused by the bundle
 *   add-to-cart flow (`productId`/`upc` come from the staged `BundleSelectionItem` instead), so it's safe.
 */
export function usePromotionPlan(id: MaybeRefOrGetter<string>) {
  const { products } = useApi()

  // query must hold plain values (client-generated types require it) - kept in sync with `id` via watch
  const query = reactive({ productId: toValue(id) })
  watch(() => toValue(id), (productId) => {
    query.productId = productId
  })

  const promotionPlanKey = computed(() => `promotionPlan-${query.productId}`)
  const { data: promotionPlan, pending: promotionPlanPending, error: promotionPlanError } = products.promotionPlan(
    query,
    {
      key: promotionPlanKey,
      lazy: true,
      server: false
    }
  )

  const bundlePromo = computed<ProductPromotion | undefined>(
    () => promotionPlan.value?.product?.find(({ type }) => type === 'BUNDLE_PROMO')
  )

  const bundleUpsells = computed<ProductUpsell[]>(
    () => bundlePromo.value?.bundleDetails?.qualifiedProducts.map((qualifiedProduct) => ({
      id: qualifiedProduct.varitionGroupId,
      image: qualifiedProduct.image,
      multiVariantsProduct: qualifiedProduct.multiVariantsProduct,
      name: qualifiedProduct.name,
      price: qualifiedProduct.price,
      showColorVariations: qualifiedProduct?.displayColorVariation,
      sku: qualifiedProduct.varitionGroupId,
      url: qualifiedProduct.productURL
    })) ?? []
  )

  return {
    promotionPlan,
    promotionPlanPending,
    promotionPlanError,
    bundlePromo,
    bundleUpsells
  }
}
